import { Box, Divider, Link, Stack } from '@mui/material';
import { Button } from 'component/MarketPlace/ButtonComponents';
import Typography from 'component/MarketPlace/Typography/Typography';
import { TruncatedText } from 'component/MarketPlace/Typography/Typography.style';
import Col from 'component/layoutComponents/Col/Col';
import Row from 'component/layoutComponents/Row/Row';
import React, { memo, useMemo, useState } from 'react';
import { Image } from '../../OrderRequestDetail.style';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';
import { ProductDetail } from '../../types';
import OrderedItemStatus from './OrderedItemStatus';
import { ORDER_ITEM_STATUS, ORDER_STATUS } from '../../constant';

export interface OrderedItemProps {
  productDetail: ProductDetail;
  onCancel?: (product: ProductDetail) => any;
  onMarkReceived?: (product: ProductDetail) => any;
  isApprovalPage?: boolean;
  locale: any;
  userMarketplace?: Record<string, any>;
  assigned_approver?: string;
  approval_status?: string;
  pendingApprovalDelegatedLocale?: string;
}

const OrderedItem = (props: OrderedItemProps) => {
  const { productDetail = {}, onMarkReceived, isApprovalPage, locale, userMarketplace, assigned_approver, approval_status, pendingApprovalDelegatedLocale } = props;
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const breakpoint = useBreakpoint();
  // const approvalStatus = APPROVAL_STATUS.EXCEPTIONAL_APPROVAL;
  // const approvalColors = getColors(approvalStatus);
  // const approvalText = STATUS_TEXT[approvalStatus];

  const isCancelStatus = productDetail.status === ORDER_STATUS.CANCELED || productDetail.status === ORDER_STATUS.CANCEL_REQUEST;

  const propsStyleBasedOnStatus = useMemo(() => {
    if (isCancelStatus) {
      return {
        color: 'text.disabled',
      };
    }
    return {
      color: 'text.primary',
    };
  }, [isCancelStatus]);

  const updateLoading = (loading: Record<string, boolean>) => {
    setLoading((prevState) => ({ ...prevState, ...loading }));
  };


  const handleMarkReceived = async () => {
    updateLoading({ markReceived: true });
    await onMarkReceived?.(productDetail);
    updateLoading({ markReceived: false });
  };

  const generateActionButtons = () => {
    const { status, trackingUrl } = productDetail;
    if (!status) return null;

    switch (status) {
      case ORDER_ITEM_STATUS.PENDING_APPROVAL:
        return null;
      case ORDER_ITEM_STATUS.ORDERED: {
        return null;
      }
      case ORDER_ITEM_STATUS.SHIPPED: {
        return (
          isApprovalPage? null : (
            <>
              <Button fullWidth loadingStart={loading.markReceived} onClick={handleMarkReceived} id="btn-productdetail-mark_received">
                {loading.markReceived ? '' : 'Mark as received'}
              </Button>
              <Button variant="outlined" fullWidth href={trackingUrl} LinkComponent={Link}>
                {locale?.trackPackage}
              </Button>
            </>
          )
        );
      }
      case ORDER_ITEM_STATUS.DELIVERED: {
        return null;
        // return (
        //   <>
        //     <Button fullWidth disabled>
        //       View in my gear
        //     </Button>
        //     <Button variant="outlined" fullWidth disabled>
        //       Report issue
        //     </Button>
        //   </>
        // );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <Box pt={1.5} pb={2}>
      <OrderedItemStatus productDetail={productDetail} userMarketplace={userMarketplace} isApprovalPage={isApprovalPage} assigned_approver={assigned_approver} approval_status={approval_status} locale={locale} pendingApprovalDelegatedLocale={pendingApprovalDelegatedLocale}/>
      <Row alignItems="center" spacing={{ xs: 2, md: 4 }} py={2} px={{ xs: 0, md: 2 }}>
        <Col xs={12} sm={8} md={7}>
          <Stack alignItems="center" direction={{ xs: 'column', sm: 'row' }} gap={4}>
            <Image src={productDetail?.pictures?.[0]?.small} alt={productDetail?.name} isDisabled={isCancelStatus} />
            <Stack justifyContent="flex-start">
              <Typography
                variant="overline"
                textTransform="uppercase"
                sx={{ ...propsStyleBasedOnStatus }}
              >
                {productDetail?.brand.name}
              </Typography>
              <Typography
                variant="h5"
                sx={{ ...propsStyleBasedOnStatus }}
                id="lbl-productdetail-name"
              >
                {productDetail?.name}
              </Typography>
              <TruncatedText 
                maxlines={2} 
                variant="caption" 
                color="text.secondary"
                sx={{ ...propsStyleBasedOnStatus }}
              >
                {productDetail?.descriptionText}
              </TruncatedText>
              {/* <EyeBrow
                fontSizeContent={12}
                color={approvalColors.type}
                label={approvalText}
                endIcon={<HelpRounded fontSize="small" sx={{ ml: -0.5 }} />}
                size="small"
                sx={{
                  width: 'fit-content',
                  fontSize: '12px',
                  margin: '7px 0 15px 0',
                  backgroundColor: approvalColors.backgroundColor,
                  color: approvalColors.color,
                  fontWeight: 600,
                }}
                variant="filled"
              /> */}
              <Stack direction="row">
                <Typography variant="caption">{locale?.reason}:&nbsp;</Typography>
                <Typography variant="caption" color="text.secondary">
                  {locale?.reasonReason}
                </Typography>
              </Stack>
              {breakpoint < Breakpoint.md && (
                <Box py={1}>
                  <Typography
                    variant="body2"
                    sx={{ ...propsStyleBasedOnStatus }}
                  >
                    {locale?.quantity}: {productDetail?.quantity}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        </Col>
        {breakpoint >= Breakpoint.md && (
          <Col xs={4} md={2}>
            <Typography variant="body2">{productDetail?.quantity}</Typography>
          </Col>
        )}
        <Col xs={12} md={3}>
          <Stack gap={1.5}>{generateActionButtons()}</Stack>
        </Col>
      </Row>
      <Divider sx={{ mt: 1.5, mb: 0 }} />
    </Box>
  );
};

export default memo(OrderedItem);
